import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Chip,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Send as SendIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { StudentSearchResult } from '../../types/student-search';
import { getPrefectureName } from '../../constants/prefectures';

interface StudentDetailModalProps {
  open: boolean;
  student: StudentSearchResult | null;
  loading: boolean;
  onClose: () => void;
  onSendScout?: (student: StudentSearchResult) => void;
}

// 日付フォーマット
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// 学習スコアからレベルを判定
const getLearningLevelFromScore = (
  score: number
): { level: 'Low' | 'Medium' | 'High'; label: string } => {
  if (score >= 70) {
    return { level: 'High', label: '高い' };
  } else if (score >= 40) {
    return { level: 'Medium', label: '普通' };
  } else {
    return { level: 'Low', label: '低い' };
  }
};

// MVPスコアレベルのラベル表示（互換性のため残す）
const getLearningLevelLabel = (level: 'Low' | 'Medium' | 'High'): string => {
  switch (level) {
    case 'High':
      return '高い';
    case 'Medium':
      return '普通';
    case 'Low':
      return '低い';
    default:
      return '不明';
  }
};

// MVPスコアレベルの色
const getLearningLevelColor = (
  level: 'Low' | 'Medium' | 'High'
): 'success' | 'warning' | 'error' => {
  switch (level) {
    case 'High':
      return 'success';
    case 'Medium':
      return 'warning';
    case 'Low':
      return 'error';
    default:
      return 'error';
  }
};

export const StudentDetailModal: React.FC<StudentDetailModalProps> = ({
  open,
  student,
  loading,
  onClose,
  onSendScout,
}) => {
  const handleScout = () => {
    if (student && onSendScout) {
      onSendScout(student);
      onClose(); // モーダルを閉じる
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5">学生詳細プロフィール</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 8,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              学生情報を読み込み中...
            </Typography>
          </Box>
        ) : student ? (
          <Box>
            {/* 基本情報ヘッダー */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                  }}
                >
                  {student.full_name.charAt(0)}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" gutterBottom>
                    {student.full_name}
                  </Typography>
                  {(student.age || student.graduation_year) && (
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      {student.age && `${student.age}歳`}
                      {student.age && student.graduation_year && ' / '}
                      {student.graduation_year && `${student.graduation_year}年卒`}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>

            <Grid container spacing={3}>
              {/* 基本情報 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <SchoolIcon />
                    基本情報
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    {student.university && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          大学
                        </Typography>
                        <Typography variant="body1">
                          {student.university}
                        </Typography>
                      </Box>
                    )}

                    {student.faculty && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          学部・学科
                        </Typography>
                        <Typography variant="body1">
                          {student.faculty}
                        </Typography>
                      </Box>
                    )}

                    {student.grade && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          学年
                        </Typography>
                        <Typography variant="body1">
                          {student.grade}年
                        </Typography>
                      </Box>
                    )}

                    {student.current_prefecture && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          居住地域
                        </Typography>
                        <Typography variant="body1">
                          {getPrefectureName(student.current_prefecture) ||
                            student.current_prefecture}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* 活動情報 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <CalendarIcon />
                    活動情報
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        最終活動日
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(student.activity.last_active_date)}
                      </Typography>
                    </Box>

                    {/* 統合学習スコア詳細 */}
                    {student.activity.learning_score && (
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            学習スコア
                          </Typography>
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="caption">
                                  • 高い: 70点以上 • 普通: 40-69点 • 低い:
                                  39点以下
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              color="action"
                              sx={{ cursor: 'help' }}
                            />
                          </Tooltip>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography variant="h6" color="primary">
                            {student.activity.learning_score.total}
                            /100
                          </Typography>
                          <Chip
                            label={
                              getLearningLevelFromScore(
                                student.activity.learning_score.total
                              ).label
                            }
                            color={getLearningLevelColor(
                              getLearningLevelFromScore(
                                student.activity.learning_score.total
                              ).level
                            )}
                            size="small"
                          />
                        </Box>
                        <Box
                          sx={{
                            mt: 1,
                            p: 1.5,
                            backgroundColor: 'grey.50',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            <strong>計算項目:</strong>{' '}
                            チャット回数、学習期間、最近の活動、プロフィール完成度
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {student.activity.learning_period && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          学習期間
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(
                            student.activity.learning_period.start_date || ''
                          )}{' '}
                          〜
                          {student.activity.learning_period.end_date
                            ? formatDate(
                                student.activity.learning_period.end_date
                              )
                            : '継続中'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* 自己PR */}
              {student.bio && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      自己PR
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {student.bio}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* 志望動機 */}
              {student.career_goal && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      志望動機・キャリア目標
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {student.career_goal}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* 営業実績・経験情報 */}
              {student.sales_experience && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <svg
                        width="24"
                        height="24"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{ color: '#1976d2' }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                      営業実績・経験情報
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={3}>
                      {/* 経験・実績 */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                          >
                            経験・実績
                          </Typography>

                          {student.sales_experience.mediation_count !==
                            undefined && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                仲介実績
                              </Typography>
                              <Typography variant="body1">
                                {student.sales_experience.mediation_count?.toLocaleString() ||
                                  0}
                                件
                              </Typography>
                            </Box>
                          )}

                          {student.sales_experience
                            .real_estate_experience_months !== undefined && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                不動産歴
                              </Typography>
                              <Typography variant="body1">
                                {student.sales_experience
                                  .real_estate_experience_months || 0}
                                ヶ月
                                {student.sales_experience
                                  .real_estate_experience_months &&
                                  ` (約${Math.floor(student.sales_experience.real_estate_experience_months / 12)}年${student.sales_experience.real_estate_experience_months % 12}ヶ月)`}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>

                      {/* 売上実績 */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                          >
                            売上実績
                          </Typography>

                          {student.sales_experience.monthly_max_sales !==
                            undefined && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                月間最高売り上げ
                              </Typography>
                              <Typography
                                variant="body1"
                                color="warning.main"
                                fontWeight="bold"
                              >
                                {Number(student.sales_experience.monthly_max_sales || 0).toLocaleString()}
                                円
                              </Typography>
                            </Box>
                          )}

                          {student.sales_experience.total_sales !==
                            undefined && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                総売り上げ
                              </Typography>
                              <Typography
                                variant="body1"
                                color="success.main"
                                fontWeight="bold"
                              >
                                {Number(student.sales_experience.total_sales || 0).toLocaleString()}
                                円
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>

                      {/* 将来の志向 */}
                      <Grid item xs={12}>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                          >
                            将来の志向
                          </Typography>

                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              起業志向
                            </Typography>
                            <Chip
                              label={
                                student.sales_experience
                                  .entrepreneurial_interest === true
                                  ? 'あり'
                                  : student.sales_experience
                                        .entrepreneurial_interest === false
                                    ? 'なし'
                                    : '未選択'
                              }
                              color={
                                student.sales_experience
                                  .entrepreneurial_interest === true
                                  ? 'primary'
                                  : student.sales_experience
                                        .entrepreneurial_interest === false
                                    ? 'default'
                                    : 'secondary'
                              }
                              variant={
                                student.sales_experience
                                  .entrepreneurial_interest === undefined
                                  ? 'outlined'
                                  : 'filled'
                              }
                              size="small"
                            />
                          </Box>
                        </Box>
                        </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}

            </Grid>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          閉じる
        </Button>
        {student && !loading && (
          <Tooltip
            title={
              student.accepts_scout_emails
                ? 'スカウトメールを送信できます'
                : 'この学生はスカウトメールの受信を停止しています'
            }
            disableHoverListener={student.accepts_scout_emails}
            arrow
          >
            <span>
              <Button
                onClick={handleScout}
                variant="contained"
                startIcon={<SendIcon />}
                disabled={!student.accepts_scout_emails}
              >
                {student.full_name}さんにスカウト送信
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};
